import { useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { useSso } from "@/context/SsoProvider";
import { useAuth } from "@/hooks/auth/useAuth";
import useLocalStorage from "@/hooks/store/useLocalStorage";
import { CheckProviderResponse } from "@/types/auth";
import { clearPkceParams, getStoredCodeVerifier } from "@/utils/pkceUtils";

/**
 * Callback for the legacy external-IdP SSO (`isLoginWithSsoEnabled`), where the
 * provider is resolved from the email domain the user typed and the app session
 * is minted by passing the IdP `id_token` through `login()`.
 *
 * Superseded by `LokasiSsoCallback` for LOKASI internal SSO; delete this once
 * the legacy flag is retired.
 */
const LegacySsoCallbackPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, login } = useAuth();
  const { ssoTokenRequest } = useSso();
  const [searchParams] = useSearchParams();
  const hasRun = useRef(false);
  const code = searchParams.get("code");
  const [savedProviderConfig, _, removeSavedProviderConfig] = useLocalStorage(
    "providerConfig",
    ""
  );
  const parsedSavedToken: CheckProviderResponse = savedProviderConfig
    ? JSON.parse(savedProviderConfig)
    : null;

  const code_verifier = getStoredCodeVerifier();

  useEffect(() => {
    if (hasRun.current) return;
    hasRun.current = true;

    const cleanupAndNavigate = (target: string) => {
      removeSavedProviderConfig();
      clearPkceParams();
      navigate(target);
    };

    if (!code || !parsedSavedToken || !code_verifier) {
      navigate("/");
    } else {
      const requestUrl = parsedSavedToken?.auth_token_url;
      ssoTokenRequest(
        {
          requestUrl,
          payload: {
            client_id: parsedSavedToken?.client_id,
            redirect_uri: parsedSavedToken?.redirect_uri,
            scope: parsedSavedToken?.scopes.join(" "),
            code,
            code_verifier,
            grant_type: "authorization_code"
          }
        },
        {
          onSuccess: (data) => {
            login(
              {
                token: data.id_token,
                email: "",
                tenant_id: parsedSavedToken.tenant_id,
                client_id: parsedSavedToken.client_id
              },
              {
                onSuccess: () => cleanupAndNavigate("/"),
                onError: () => cleanupAndNavigate("/login")
              }
            );
          },
          onError: (error) => {
            console.error(error);
            cleanupAndNavigate("/login");
          }
        }
      );
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/");
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return <div>Authorizing...</div>;
};

export default LegacySsoCallbackPage;
